/// <amd-module name="VSS/Diagnostics/Dialog/DiagnosticsListPivotContent" />
import React = require("react");

import { ClientContribution as Contribution } from "VSS/Platform/Contracts/Contributions";
import { ContributionLink } from "VSS/Diagnostics/Dialog/DiagnosticsDialog.types";
import { getAssetLocation } from "VSS/Platform/Location";
import { GroupedList } from "OfficeFabric/GroupedList";
import { IColumn } from "OfficeFabric/DetailsList";
import { IEvaluatedCondition } from "VSS/Platform/Page";
import { IVssComponentProperties, VssComponent, IVssComponentContext } from "VSS/Platform/Layout";
import { memoizeFunction } from "VSS/Core/Util/Memoize";
import { VssPersona } from "VSSUI/VSSPersona";
import { PickList, IPickListSelection } from "VSSUI/PickList";
import { SelectionMode, ISelection, Selection, IObjectWithKey } from "OfficeFabric/Selection";
import { Splitter } from "VSSUI/Splitter";
import { VssDetailsList, IVssDetailsList } from "VSSUI/VssDetailsList";
import { VssIconType, VssIcon } from "VSSUI/VssIcon";
import { ZeroData } from "VSSUI/ZeroData";

export interface PerfDiagnosticsListPivotContentProps extends IVssComponentProperties {
    filteredItems: Contribution[];
    contributions: Contribution[];
    allContributions: Contribution[];
    targets: { [contributionId: string]: ContributionLink[] };
    includes: { [contributionId: string]: ContributionLink[] };
}

export interface PerfDiagnosticsListPivotContentState extends IVssComponentProperties {
    selectedItem: Contribution | null;
    selection: ISelection;
    columns: IColumn[];
}

export class PerfDiagnosticsListPivotContent extends VssComponent<PerfDiagnosticsListPivotContentProps, PerfDiagnosticsListPivotContentState> {
    private detailsList: IVssDetailsList;

    constructor(props: PerfDiagnosticsListPivotContentProps, context: IVssComponentContext) {
        super(props, context);

        const { contributions } = this.props;

        const itemDictionary: { [contributionId: string]: Contribution } = {};

        contributions.forEach(function (element) {
            itemDictionary[element.id] = element;
        });

        const columns: IColumn[] = [
            {
                key: "status",
                fieldName: "status",
                name: "Status",
                onRender: item => this.getIcon(item),
                minWidth: 36,
                maxWidth: 36,
                isResizable: false,
                className: "diag-dialog-arrange-icons"
            },
            {
                key: "id",
                fieldName: "id",
                name: "Id",
                minWidth: 300,
                maxWidth: 500,
                isResizable: true,
                isSortedDescending: true
            },
            {
                key: "type",
                fieldName: "type",
                name: "Type",
                onRender: (item: Contribution) => (
                    <div className="flex-row flex-baseline">
                        <VssPersona className="diag-dialog-persona margin-right-4" displayName={this.getInitials(item.id)} size="small" />
                        <span>{item.type}</span>
                    </div>
                ),
                minWidth: 200,
                maxWidth: 400,
                isResizable: true,
                isSortedDescending: true
            }
        ];
        this.itemDictionary = itemDictionary;
        this.containsDictionary = itemDictionary;

        this.state = {
            selectedItem: null,
            selection: new Selection({
                getKey: item => {
                    return (item as Contribution).id;
                },
                selectionMode: SelectionMode.single
            }),
            columns: columns
        };
    }

    itemDictionary: { [contributionId: string]: Contribution };
    containsDictionary: { [contributionId: string]: Contribution };
    sortedItems: Contribution[] = this.props.filteredItems;

    public render(): JSX.Element {
        const { selection, selectedItem, columns } = this.state;
        const { includes, targets, filteredItems, allContributions, contributions } = this.props;

        this.itemDictionary = this.updateItemDictionary(allContributions);
        this.sortedItems = this.updateSortedList(filteredItems, columns);
        this.containsDictionary = this.updateContainsDictionary(contributions);

        return (
            <div className="flex flex-grow flex-column">
                <Splitter
                    nearElementClassName="flex flex-column"
                    onRenderNearElement={() => {
                        return (
                            <div className="flex flex-grow justify-center diag-dialog-low-flex-basis diag-dialog-splitter-left">
                                {this.sortedItems.length === 0 ? (
                                    <ZeroData
                                        primaryText="There are no entries that match these filters!"
                                        secondaryText={
                                            <span>You can modify the which contributions you are filtering by accessing the view options.</span>
                                        }
                                        imageAltText="Chart"
                                        imagePath={getAssetLocation(
                                            this.context.pageContext,
                                            "ms.vss-features/diag-dialog-content/images/zero-data-image.png"
                                        )}
                                    />
                                ) : (
                                    <VssDetailsList
                                        className="h-scroll-hidden"
                                        selectionPreservedOnEmptyClick={true}
                                        items={this.sortedItems}
                                        columns={columns}
                                        onColumnHeaderClick={this.onColumnClick}
                                        selection={selection}
                                        onActiveItemChanged={this.changeSelectedItem}
                                        componentRef={(detailsList: IVssDetailsList) => {
                                            this.detailsList = detailsList;
                                        }}
                                    />
                                )}
                            </div>
                        );
                    }}
                    farElementClassName="flex flex-column"
                    onRenderFarElement={() => {
                        return (
                            <div className="v-scroll-auto flex flex-grow diag-dialog-low-flex-basis flex-column diag-dialog-splitter-right">
                                {selectedItem ? (
                                    <div className="flex flex-grow flex-column">
                                        <div className="font-size-lll font-weight-light">
                                            {this.getIcon(selectedItem)} {selectedItem.id}
                                        </div>
                                        <div className="font-size-l font-weight-light">{selectedItem.type ? selectedItem.type : "No Type"}</div>
                                        {targets[selectedItem.id] ? <h4>Targets</h4> : <div />}
                                        <PickList
                                            className="flex-noshrink"
                                            items={targets[selectedItem.id]}
                                            onSelectionChanged={this.selectSourceById}
                                            getListItem={(item: ContributionLink) => {
                                                return {
                                                    name: item.parentContributionId,
                                                    key: item.parentContributionId,
                                                    iconProps: this.isActiveContribution(item.parentContributionId)
                                                        ? {
                                                              iconType: VssIconType.fabric,
                                                              iconName: "CheckMark",
                                                              className: "diag-dialog-is-not-constrained font-size-ml"
                                                          }
                                                        : { iconType: VssIconType.fabric, iconName: "Cancel", className: "font-size-ml" }
                                                };
                                            }}
                                        />
                                        {includes[selectedItem.id] ? <h4>Targeted By</h4> : <div />}
                                        <PickList
                                            className="flex-noshrink"
                                            items={includes[selectedItem.id]}
                                            onSelectionChanged={this.selectTargetById}
                                            getListItem={(item: ContributionLink) => {
                                                return {
                                                    name: item.childContributionId,
                                                    key: item.childContributionId,
                                                    iconProps: this.isActiveContribution(item.childContributionId)
                                                        ? {
                                                              iconType: VssIconType.fabric,
                                                              iconName: "CheckMark",
                                                              className: "diag-dialog-is-not-constrained"
                                                          }
                                                        : { iconType: VssIconType.fabric, iconName: "Cancel", className: "font-size-ml" }
                                                };
                                            }}
                                        />
                                        {targets[selectedItem.id] ? (
                                            <div>
                                                <h4>Condition Info</h4>
                                                {this.getAllConditions(selectedItem.id)}
                                            </div>
                                        ) : (
                                            <div />
                                        )}
                                        <h4>Properties</h4>
                                        <div className="diag-dialog-properties">{this.getPropertiesFromObject(selectedItem.properties, 0)}</div>
                                    </div>
                                ) : (
                                    <div />
                                )}
                            </div>
                        );
                    }}
                />
            </div>
        );
    }

    public componentDidMount() {
        this.setState(
            {
                selectedItem: this.sortedItems[0]
            },
            this.matchSelectionToSelectedItem
        );
    }

    private matchSelectionToSelectedItem() {
        const { selection, selectedItem } = this.state;
        if (selectedItem) {
            selection.setItems(this.sortedItems as IObjectWithKey[], true);
            selection.setKeySelected(selectedItem.id, true, true);
        }
        this.updateFocus();
    }

    private updateSortedList = memoizeFunction((filteredItems, columns) => {
        const currColumn: IColumn = columns.filter((currCol: IColumn) => {
            return currCol.isSorted;
        })[0];
        if (!currColumn) {
            return filteredItems;
        }
        return this.sortItems(filteredItems, currColumn.fieldName as keyof Contribution, currColumn.isSortedDescending, currColumn.isSorted);
    });

    private updateItemDictionary = memoizeFunction((allContributions: Contribution[]) => {
        if (!allContributions.length) {
            return this.itemDictionary;
        }
        const itemDictionary: { [contributionId: string]: Contribution } = {};
        allContributions.forEach(function (element) {
            itemDictionary[element.id] = element;
        });
        return itemDictionary;
    });

    private updateContainsDictionary = memoizeFunction((contributions: Contribution[]) => {
        const containsDictionary: { [contributionId: string]: Contribution } = {};
        for (let element of contributions) {
            containsDictionary[element.id] = element;
        }
        return containsDictionary;
    });

    private onColumnClick = (ev: React.MouseEvent<HTMLElement>, column: IColumn) => {
        if (column.key === "status") {
            return;
        }
        let newItems: Contribution[] = [...this.props.filteredItems];
        const newColumns: IColumn[] = [...this.state.columns];
        const currColumn: IColumn = newColumns.filter((currCol: IColumn) => {
            return column.key === currCol.key;
        })[0];
        newColumns.forEach((newCol: IColumn) => {
            if (newCol.key === currColumn.key) {
                currColumn.isSortedDescending = !currColumn.isSortedDescending;
                currColumn.isSorted = true;
            } else {
                newCol.isSorted = false;
                newCol.isSortedDescending = true;
            }
        });
        this.sortItems(newItems, currColumn.fieldName as keyof Contribution, currColumn.isSortedDescending, currColumn.isSorted);
        this.setState(
            {
                columns: newColumns
            },
            this.matchSelectionToSelectedItem
        );
    };

    private sortItems(items: Contribution[], sortBy: keyof Contribution, descending = false, isSorted: boolean = false) {
        let newItems;
        if (isSorted) {
            const sortNum = descending ? 1 : -1;
            newItems = items.sort((a: Contribution, b: Contribution) => {
                if (a[sortBy] < b[sortBy]) {
                    return 1 * sortNum;
                }
                if (a[sortBy] > b[sortBy]) {
                    return -1 * sortNum;
                }
                return 0;
            });
        } else {
            newItems = items;
        }
        return newItems;
    }

    private selectSourceById = (pickSelection: IPickListSelection) => {
        const id = pickSelection.selectedItems[0].parentContributionId;
        this.selectContribution(id);
    };

    private selectTargetById = (pickSelection: IPickListSelection) => {
        const id = pickSelection.selectedItems[0].childContributionId;
        this.selectContribution(id);
    };

    private changeSelectedItem = (item: Contribution) => {
        const { selectedItem, selection } = this.state;
        if (!selectedItem || (selection.getSelection()[0] && selectedItem.id !== (selection.getSelection()[0] as Contribution).id)) {
            this.setState({ selectedItem: item }, this.updateFocus);
        }
    };

    private selectContribution(id: string) {
        const { selection } = this.state;
        if (selection.getSelectedIndices) {
            selection.setIndexSelected(selection.getSelectedIndices()[0], false, false);
        }
        selection.setKeySelected(id, true, true);
        this.setState(
            {
                selectedItem: this.itemDictionary[id]
            },
            this.updateFocus
        );
    }

    private getSelectedIndex = () => {
        const { selectedItem } = this.state;
        if (selectedItem) {
            return this.sortedItems.findIndex(function (element: Contribution) {
                return selectedItem.id === element.id;
            });
        }
    };

    private updateFocus() {
        if (this.detailsList.detailsList) {
            const index = this.getSelectedIndex();
            if (index != undefined) {
                this.detailsList.detailsList.focusIndex(index);
            }
        }
    }

    private getPropertiesFromObject(properties: any, indentation: number) {
        let rows: JSX.Element[] = [];
        for (const property in properties) {
            if (properties[property] == null) {
                rows.push(<pre key={property + indentation + 1}>{`${this.getStringFromIndentation(indentation) + property}: null`}</pre>);
            } else if (typeof properties[property] === "object") {
                rows.push(<pre key={property + indentation + 2}>{`${this.getStringFromIndentation(indentation) + property} {`}</pre>);
                rows.push(<div key={property + indentation + 3}>{this.getPropertiesFromObject(properties[property], indentation + 2)}</div>);
                rows.push(<pre key={property + indentation + 4}>{`${this.getStringFromIndentation(indentation)}}`}</pre>);
            } else {
                rows.push(
                    <pre key={property + indentation + 5}>{`${this.getStringFromIndentation(indentation) + property}: ${properties[property]}`}</pre>
                );
            }
        }
        return rows;
    }

    private getStringFromIndentation(indentation: number) {
        let spacing = "";
        for (let i = 0; i < indentation; i++) {
            spacing += "  ";
        }
        return spacing;
    }

    private getIcon(item: Contribution) {
        if (!this.containsDictionary[item.id]) {
            return <VssIcon iconType={VssIconType.fabric} iconName="ErrorBadge" className="diag-dialog-is-not-in-page font-size-l" />;
        }
        if (this.isActiveContribution(item.id)) {
            return <VssIcon iconType={VssIconType.fabric} iconName="CheckMark" className="diag-dialog-is-not-constrained font-size-l" />;
        }
        return <VssIcon iconType={VssIconType.fabric} iconName="Cancel" className="font-size-l" />;
    }

    private getInitials(id: string) {
        const type = this.itemDictionary[id].type;
        if (type === "") {
            return "NT";
        }
        const split = type.split(".");
        const splitEnd = split[split.length - 1].split("-");
        if (splitEnd.length === 1) {
            return splitEnd[0].substring(0, 2).toLocaleUpperCase();
        }
        let initials = "";
        for (let i = 0; i < 2; i++) {
            initials += splitEnd[i].charAt(0);
        }
        return initials.toLocaleUpperCase();
    }

    private getEvalFilterIcons(evaluation: boolean, filtered: boolean) {
        let icons: JSX.Element[] = [];

        if (filtered) {
            icons.push(
                <VssIcon
                    key="Hide"
                    iconType={VssIconType.fabric}
                    iconName="ClearFilter"
                    className="diag-dialog-condition-icon-padding font-size-ml"
                />
            );
        } else {
            icons.push(
                <VssIcon
                    key="Hide"
                    iconType={VssIconType.fabric}
                    iconName="ClearFilter"
                    className="diag-dialog-condition-icon-padding font-size-ml diag-dialog-white-text"
                />
            );
        }
        if (evaluation) {
            icons.push(
                <VssIcon
                    key="CheckMark"
                    iconType={VssIconType.fabric}
                    iconName="CheckMark"
                    className="diag-dialog-condition-icon-padding font-size"
                />
            );
        } else {
            icons.push(
                <VssIcon key="Cancel" iconType={VssIconType.fabric} iconName="Cancel" className="diag-dialog-condition-icon-padding font-size" />
            );
        }
        return icons;
    }

    private isActiveContribution(id: string): boolean {
        const { targets } = this.props;
        if (!targets[id]) {
            return true;
        }
        return targets[id].some(e => e.outputOptions !== 0);
    }

    private getAllConditions(id: string): JSX.Element[] {
        const { targets } = this.props;
        const selectedRelationships = targets[id];
        let conditions: JSX.Element[] = [];
        if (selectedRelationships !== undefined) {
            for (let i = 0; i < selectedRelationships.length; i++) {
                const conditionsList = selectedRelationships[i].conditions;
                if (conditionsList) {
                    conditions.push(
                        <GroupedList
                            key={"Conditions" + selectedRelationships[i].parentContributionId}
                            className="diag-dialog-condition"
                            items={conditionsList}
                            selectionMode={SelectionMode.none}
                            groupProps={{
                                headerProps: {
                                    onGroupHeaderClick: group => {
                                        group.isCollapsed = !group.isCollapsed;
                                    },
                                    selectionMode: SelectionMode.none
                                }
                            }}
                            onRenderCell={(nestingDepth: number, item: IEvaluatedCondition) => {
                                return (
                                    <div className="flex flex-row diag-dialog-conditions-list">
                                        {this.getEvalFilterIcons(item.evaluation, item.filtered)}
                                        {typeof item.details === "string" ? (
                                            <div className="diag-dialog-side-list-padding">{item.details}</div>
                                        ) : (
                                            <div className="diag-dialog-side-code-padding">{this.getPropertiesFromObject(item.details, 0)}</div>
                                        )}
                                    </div>
                                );
                            }}
                            groups={[
                                {
                                    key: selectedRelationships[i].parentContributionId,
                                    count: conditionsList.length,
                                    name: "Target: " + selectedRelationships[i].parentContributionId,
                                    startIndex: 0,
                                    isCollapsed: true
                                }
                            ]}
                        />
                    );
                }
            }
        }
        return conditions;
    }
}
